import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import PublicNav from "@/components/PublicNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const inputCls =
  "mt-1.5 bg-white border-zinc-400 rounded-sm font-mono focus-visible:ring-[#5EA529] text-zinc-900";

export default function Contacto() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
  const canSubmit =
    form.name.trim() && emailOk && form.message.trim().length >= 5 && !loading;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    try {
      await api.post("/contact", {
        name: form.name.trim(),
        email: form.email.trim(),
        message: form.message.trim(),
      });
      setSent(true);
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#D6D3D1] text-zinc-900">
      <PublicNav />
      <main className="max-w-2xl mx-auto px-5 py-10">
        <p className="text-xs uppercase tracking-[0.3em] text-black/60 font-semibold mb-2">
          Radio Club Ochentero · Contacto
        </p>
        <h1 className="font-mono text-3xl sm:text-4xl font-bold tracking-tighter text-black leading-none inline-block border-b-[8px] border-[#5EA529] pb-1 mb-4">
          Escríbenos
        </h1>
        <p className="text-sm text-zinc-700 leading-relaxed mb-8">
          ¿Dudas sobre un diploma, un contacto que no aparece, colaboraciones o cualquier
          cosa? Rellena el formulario y te contestaremos al correo que nos indiques.
        </p>

        {sent ? (
          <div
            data-testid="contact-sent-message"
            className="rounded-md border border-[#5EA529] bg-white p-6 flex items-start gap-3"
          >
            <CheckCircle2 className="h-6 w-6 text-[#5EA529] shrink-0 mt-0.5" />
            <div>
              <h2 className="font-mono font-bold text-black text-lg">
                Mensaje enviado
              </h2>
              <p className="text-sm text-zinc-700 mt-1">
                Gracias por escribirnos. Hemos guardado tu mensaje y te contestaremos lo
                antes posible.
              </p>
              <Button
                onClick={() => setSent(false)}
                variant="outline"
                className="mt-4 border-zinc-400 bg-white hover:bg-zinc-100 rounded-sm"
                data-testid="contact-send-another"
              >
                Enviar otro mensaje
              </Button>
            </div>
          </div>
        ) : (
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="rounded-md border border-zinc-400 bg-white p-6 space-y-4"
          >
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-zinc-600">
                Nombre o indicativo
              </Label>
              <Input
                data-testid="contact-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className={inputCls}
                placeholder="EA3XYZ / Juan"
                maxLength={80}
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-zinc-600">
                Email
              </Label>
              <Input
                data-testid="contact-email-input"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={inputCls}
                placeholder="tu@email.com"
                maxLength={120}
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-zinc-600">
                Mensaje
              </Label>
              <Textarea
                data-testid="contact-message-input"
                rows={6}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className={inputCls}
                placeholder="Cuéntanos..."
                maxLength={4000}
              />
              <p className="text-xs text-zinc-500 mt-1 font-mono">
                {form.message.length} / 4000
              </p>
            </div>
            <Button
              type="submit"
              data-testid="contact-submit-btn"
              disabled={!canSubmit}
              className="w-full bg-[#5EA529] hover:bg-[#4f8c22] text-white font-semibold rounded-sm h-12 disabled:opacity-40"
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" /> Enviar mensaje
                </>
              )}
            </Button>
          </form>
        )}
      </main>
    </div>
  );
}
